/**
 * /signup/mandate-return — Trial-mandate confirmation page.
 *
 * Customer lands here after Cashfree's hosted-auth page redirects
 * back. We poll POST /api/subscriptions/trial/confirm/<sub_id>/ which
 * pulls authoritative subscription state from Cashfree and, if the
 * mandate is authorised, flips the trial Subscription PENDING →
 * ACTIVE + credits 100 ZC.
 *
 * Subscription id is read from sessionStorage (set by Signup.jsx
 * before the Cashfree redirect) OR from the `?sub=` query string as
 * a fallback if the session is gone (private window / clear-on-exit
 * browser settings).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import { confirmTrialMandate, regenerateTrialMandate } from "../../api/trial.api";


const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 40;  // 3s × 40 = 120s
// Tolerate 4xx for the first N attempts before declaring failure —
// Cashfree's `/subscriptions/{id}` lookup can transiently 4xx on a
// just-created sub before mandate state propagates. Backend wraps
// that as 400 gateway_rejected; we don't want to surface "couldn't
// confirm" on a poll that's just early.
const TRANSIENT_4XX_ATTEMPTS = 10;  // 3s × 10 = 30s grace


function readStashed() {
  try {
    const raw = sessionStorage.getItem("zcd_trial_mandate");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}


// Lazy-load Cashfree's v3 JS SDK exactly once. The SDK exposes a
// window.Cashfree constructor which we invoke with subscriptionSessionId
// to open the hosted-auth page. Idempotent — repeat calls no-op.
let _cashfreeSdkPromise = null;
function ensureCashfreeSdkLoaded() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.Cashfree) return Promise.resolve();
  if (_cashfreeSdkPromise) return _cashfreeSdkPromise;
  _cashfreeSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      _cashfreeSdkPromise = null;
      reject(new Error("Cashfree SDK failed to load"));
    };
    document.head.appendChild(script);
  });
  return _cashfreeSdkPromise;
}


function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}


export default function MandateReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 2026-06-23 — capture subId + stashed metadata ONCE at mount via
  // lazy useState init. They must NOT be derived inline because the
  // polling success path clears sessionStorage; if subId were
  // re-derived on every render, the post-clear render would see
  // sessionStorage as empty, recompute subId=0, and trip the
  // `if (!subId)` branch of the polling effect — overwriting the
  // "success" state with "Couldn't confirm mandate" immediately
  // after success. Freezing both values prevents that.
  const [subId] = useState(() => Number(
    searchParams.get("sub") || readStashed()?.subscription_id || 0,
  ));
  const [stashed] = useState(() => readStashed() || {});
  const targetPlanCode = stashed?.target_plan_code || "";
  const trialEndsAt = stashed?.trial_ends_at || "";

  const [state, setState] = useState("polling");   // polling | success | failed | retry_full
  const [errorMsg, setErrorMsg] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState("");
  const stopRef = useRef(false);

  // "Restart authorisation" — regenerate the Cashfree session on a
  // stuck PENDING trial, then re-open the hosted-auth page. Uses
  // Cashfree's JS SDK loaded on-demand; the auth URL is a v3 hosted
  // page whose format varies by merchant so we hand the session_token
  // to the SDK instead of constructing a URL server-side.
  async function handleRegenerate() {
    if (!subId) return;
    setRegenerating(true);
    setRegenError("");
    try {
      const res = await regenerateTrialMandate(subId, {
        returnUrl: `${window.location.origin}/signup/mandate-return`,
      });
      const data = res?.data || {};
      // Prefer the SDK path when Cashfree returns a session_token; fall
      // back to the direct authorisation_url if only that came back.
      if (data.session_token) {
        try { await ensureCashfreeSdkLoaded(); } catch { /* fall through */ }
        const CF = window.Cashfree;
        if (CF?.subscriptionsCheckout) {
          CF.subscriptionsCheckout({ subscriptionSessionId: data.session_token });
          return;
        }
      }
      if (data.authorisation_url) {
        window.location.href = data.authorisation_url;
        return;
      }
      setRegenError(
        "Could not open Cashfree's authorisation page. Please contact support.",
      );
    } catch (e) {
      const msg = e?.response?.data?.message || "";
      setRegenError(
        msg || "Couldn't restart authorisation. Please try again in a moment.",
      );
    } finally {
      setRegenerating(false);
    }
  }

  useEffect(() => {
    return () => { stopRef.current = true; };
  }, []);

  useEffect(() => {
    if (!subId) {
      setState("failed");
      setErrorMsg(
        "We can't tell which trial to confirm. If you just signed up, " +
        "try refreshing — otherwise head to your account.",
      );
      return undefined;
    }

    let cancelled = false;
    const tick = async (n) => {
      if (cancelled || stopRef.current) return;
      try {
        const res = await confirmTrialMandate(subId);
        const data = res?.data || {};
        if (data.mandate_authorised) {
          setState("success");
          try { sessionStorage.removeItem("zcd_trial_mandate"); } catch { /* ignore */ }
          return;
        }
        // Mandate not yet authorised on Cashfree's side — keep polling.
      } catch (e) {
        const code = e?.response?.data?.error || "";
        const status = e?.response?.status;
        if (status === 503 || code === "provider_unavailable") {
          // Transient — keep polling. Cashfree is being slow.
        } else if (
          (status === 400 || status === 409)
          && n < TRANSIENT_4XX_ATTEMPTS
        ) {
          // Early 4xx — could be Cashfree's status lookup racing the
          // just-created sub. Keep polling for the grace window.
        } else if (status === 400 || status === 409) {
          // Persistent 4xx — sub isn't a mandate trial, no Cashfree
          // mandate, terminal state, or sustained gateway rejection.
          setState("failed");
          setErrorMsg(
            e?.response?.data?.message
              || "We couldn't confirm your mandate. Please contact support.",
          );
          return;
        }
        // Unknown error — fall through to next attempt.
      }

      setAttempts(n + 1);
      if (n + 1 >= MAX_POLL_ATTEMPTS) {
        setState("retry_full");
        return;
      }
      setTimeout(() => tick(n + 1), POLL_INTERVAL_MS);
    };
    tick(0);

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);

  return (
    <div className="min-h-screen bg-bg-main text-text-primary flex flex-col items-center justify-center px-6 py-10">
      <Link to="/" className="font-bold text-xl tracking-tight mb-8">
        Zen<span className="text-button-primary">cricket</span>data
      </Link>

      <div className="w-full max-w-md bg-bg-primary border border-border-default rounded-2xl p-8 sm:p-10 text-center">
        {state === "polling" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-button-primary/10 flex items-center justify-center mb-5">
              <Loader2 className="w-7 h-7 text-button-primary animate-spin" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">
              Confirming your mandate…
            </h1>
            <p className="text-text-secondary text-sm">
              Cashfree is finishing the authorisation. This usually takes a
              few seconds.
            </p>
            <p className="text-[11px] text-text-secondary mt-3 opacity-70">
              Attempt {attempts + 1} of {MAX_POLL_ATTEMPTS}
            </p>
          </>
        )}

        {state === "success" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">
              Mandate authorised
            </h1>
            <p className="text-text-secondary text-sm">
              Your 30-day trial is live with{" "}
              <strong className="text-text-primary">100 Zencredits</strong>{" "}
              in your wallet.
            </p>
            {targetPlanCode && (
              <div className="mt-5 px-3.5 py-2.5 rounded-lg text-sm bg-emerald-500/5 border border-emerald-500/30 text-emerald-200">
                Your first month on <strong>{targetPlanCode.replace(/_/g, " ")}</strong>
                {trialEndsAt && (
                  <> will auto-charge on <strong>{fmtDate(trialEndsAt)}</strong></>
                )}
                . Cancel anytime before then for no charge.
              </div>
            )}
            <button
              onClick={() => navigate("/check-your-email", { state: { email: stashed?.email || "" } })}
              className="mt-6 w-full bg-button-primary hover:brightness-110 text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              Verify your email next <ArrowRight className="w-4 h-4" />
            </button>
            <Link
              to="/dashboard"
              className="mt-3 inline-block text-sm text-text-secondary hover:text-text-primary"
            >
              Skip to dashboard
            </Link>
          </>
        )}

        {state === "retry_full" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/15 flex items-center justify-center mb-5">
              <AlertTriangle className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">
              Mandate still processing
            </h1>
            <p className="text-text-secondary text-sm">
              Cashfree hasn't confirmed the mandate yet. If you closed the
              authorisation page or the OTP timed out, restart it below.
              Otherwise you can wait — UPI Autopay and e-NACH sometimes
              take 1-2 minutes to complete.
            </p>
            {regenError && (
              <p className="mt-3 text-xs text-rose-400">{regenError}</p>
            )}
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="mt-6 w-full bg-button-primary hover:brightness-110 disabled:opacity-60 text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Opening Cashfree…
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" /> Restart authorisation
                </>
              )}
            </button>
            <button
              onClick={() => {
                setAttempts(0);
                setState("polling");
              }}
              disabled={regenerating}
              className="mt-3 w-full bg-bg-secondary hover:brightness-110 disabled:opacity-60 text-text-primary font-semibold py-2.5 rounded-lg transition-all text-sm"
            >
              Check again
            </button>
            <Link
              to="/dashboard"
              className="mt-3 inline-block text-sm text-text-secondary hover:text-text-primary"
            >
              Go to dashboard
            </Link>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-rose-500/15 flex items-center justify-center mb-5">
              <AlertTriangle className="w-7 h-7 text-rose-400" />
            </div>
            <h1 className="text-2xl font-extrabold tracking-tight mb-2">
              Couldn't confirm mandate
            </h1>
            <p className="text-text-secondary text-sm">{errorMsg}</p>
            <Link
              to="/dashboard"
              className="mt-6 inline-block w-full bg-button-primary hover:brightness-110 text-white font-semibold py-3 rounded-lg"
            >
              Go to dashboard
            </Link>
            <Link
              to="/login"
              className="mt-3 inline-block text-sm text-text-secondary hover:text-text-primary"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
